import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { FileMetaData, FileQueryKey, FileQueryOptions } from '../hooks/use-file';

import { useFile } from '../hooks/use-file';
import { File } from './file';

export type FilePreviewProps<TData extends Blob | null, TError = unknown, TQueryKey extends FileQueryKey = FileQueryKey> = {
    queryOptions: FileQueryOptions<TData, TError, TQueryKey>;
    metaData?: FileMetaData;
    disabled?: boolean;
};

export function FilePreview<TData extends Blob | null, TError = unknown, TQueryKey extends FileQueryKey = FileQueryKey>(props: FilePreviewProps<TData, TError, TQueryKey>) {
    const { t } = useTranslation('common');

    const { queryOptions, metaData, disabled } = props;

    const { data, isLoading, isError } = useFile({ queryOptions, metaData });

    const isDisabled = useMemo(() => {
        if (disabled)
            return true;
        if (isLoading)
            return true;
        if (isError)
            return true;
        if (!data)
            return true;
        return false;
    }, [data, isLoading, isError, disabled]);

    return (
        <div className="w-full flex flex-col gap-2">
            <File file={isDisabled ? null : data as File} {...props} disabled={isDisabled} />
            {!isLoading && isError && <p className="text-red-500">{t('file-preview.error')}</p>}
        </div>
    );
}
